import {
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgClass } from '@angular/common';
import { forkJoin, map, of, switchMap } from 'rxjs';
import { ModalComponent } from '../../../shared/components/modal/modal';
import { SelectComponent, SelectOption } from '../../../shared/components/select/select';
import {
  DateRangePickerComponent,
  DateRange,
} from '../../../shared/components/date-range-picker/date-range-picker';
import { DataElementService } from '../../../core/services/data-element.service';
import { ProgramService } from '../../../core/services/program.service';
import { ToastService } from '../../../core/services/toast.service';
import { TaxonomyService } from '../../../core/services/taxonomy.service';
import { TaxonomyMemberService } from '../../../core/services/taxonomy-member.service';
import { CdeSituationTaxonomyMember } from '../../../shared/models/taxonomy-member.models';
import {
  CdeListItem,
  CdeProgramEntry,
  ConfiguredDataElementListItem,
  Program,
  ProgramType,
} from '../../../shared/models/program.models';
import { apiErrorMessage } from '../../../shared/utils/api-error';

@Component({
  selector: 'app-edit-data-element-modal',
  imports: [ModalComponent, NgClass, SelectComponent, DateRangePickerComponent],
  templateUrl: './edit-data-element-modal.html',
})
export class EditDataElementModalComponent implements OnInit {
  element = input.required<ConfiguredDataElementListItem>();
  closed = output<void>();
  saved = output<void>();
  removed = output<void>();

  private dataElementService = inject(DataElementService);
  private programService = inject(ProgramService);
  private toastService = inject(ToastService);
  private taxonomyService = inject(TaxonomyService);
  private taxonomyMemberService = inject(TaxonomyMemberService);
  private destroyRef = inject(DestroyRef);

  private initialValues = signal<{
    name: string;
    definition: string;
    dataTypeId: string;
    listItems: string;
    entries: string;
    categories: string;
  } | null>(null);

  protected dataTypeOptions = signal<SelectOption[]>([]);
  protected personTypeOptions = signal<SelectOption[]>([]);
  protected openLevelEntryProgramId = signal<string | null>(null);
  protected openPITypeEntryProgramId = signal<string | null>(null);
  protected programTypes = signal<ProgramType[]>([]);
  protected programs = signal<Program[]>([]);
  protected programTypeFilter = signal('');
  protected loadingPrograms = signal(false);

  protected name = signal('');
  protected definition = signal('');
  protected dataTypeId = signal('');
  protected listItems = signal<CdeListItem[]>([]);
  protected cdeProgramEntries = signal<CdeProgramEntry[]>([]);

  // Situation categories linked to this CDE (JCCA-159, two-way). The section shows only once the
  // API returns `cdeSituationTaxonomyMembers`, so an older API can't wipe links on save.
  protected categoriesSupported = signal(false);
  protected categoriesTab = signal(false);
  protected categoryLinks = signal<CdeSituationTaxonomyMember[]>([]);
  /** Taxonomy member id → "Category (Taxonomy)". */
  protected categoryLabels = signal<Map<string, string>>(new Map());
  protected loadingCategories = signal(false);
  protected newItemRange = signal<{ from: string; to: string }>({ from: '', to: '' });
  protected showErrors = signal(false);
  protected submitting = signal(false);
  protected removing = signal(false);
  protected confirmRemove = signal(false);
  protected loading = signal(true);

  protected editingIndex = signal<number | null>(null);
  protected editingValue = signal('');
  protected dragOverIndex = signal<number | null>(null);
  protected dragIndex: number | null = null;

  protected hasChanges = computed(() => {
    const iv = this.initialValues();
    if (!iv) return false;
    const currentListItems = JSON.stringify(this.listItems().map((i) => i.value));
    const currentEntries = JSON.stringify(
      [...this.cdeProgramEntries()]
        .map((e) => ({
          id: e.programId,
          b: e.beginDate,
          en: e.endDate,
          l: e.levelIncidentPI,
          pi: e.piInvolvementTypeId,
          r: e.requiredOptional,
        }))
        .sort((a, b) => a.id.localeCompare(b.id)),
    );
    return (
      this.name().trim() !== iv.name ||
      this.definition() !== iv.definition ||
      this.dataTypeId() !== iv.dataTypeId ||
      currentListItems !== iv.listItems ||
      currentEntries !== iv.entries ||
      categoryKey(this.categoryLinks()) !== iv.categories
    );
  });

  protected selectedTypeIsRecording = computed(() => {
    const type = this.programTypes().find((t) => t.id === this.programTypeFilter());
    return type?.code.toLowerCase() === 'ir' || type?.code.toLowerCase() === 'rec';
  });

  // Entries whose program belongs to the currently selected type
  protected filteredEntries = computed(() => {
    const programIds = new Set(this.programs().map((p) => p.id));
    return this.cdeProgramEntries().filter((e) => programIds.has(e.programId));
  });

  protected availablePrograms = computed<SelectOption[]>(() =>
    this.programs()
      .filter((p) => !this.cdeProgramEntries().some((e) => e.programId === p.id))
      .map((p) => ({ value: p.id, label: p.name })),
  );

  protected availableCategories = computed<SelectOption[]>(() => {
    const linked = new Set(this.categoryLinks().map((l) => l.situationTaxonomyMemberId));
    return [...this.categoryLabels()]
      .filter(([id]) => !linked.has(id))
      .map(([id, label]) => ({ value: id, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  });

  protected listInputType = computed<'text' | 'date' | 'number'>(() => {
    const label =
      this.dataTypeOptions()
        .find((o) => o.value === this.dataTypeId())
        ?.label.toLowerCase() ?? '';
    if (label.includes('date')) return 'date';
    if (label.includes('int') || label.includes('float') || label.includes('number'))
      return 'number';
    return 'text';
  });

  protected showListSection = computed(() => {
    const label =
      this.dataTypeOptions()
        .find((o) => o.value === this.dataTypeId())
        ?.label.toLowerCase() ?? '';
    return label === 'list item';
  });

  ngOnInit(): void {
    forkJoin({
      element: this.dataElementService.getDataElementById(this.element().id),
      dataTypes: this.dataElementService.getCdeDataTypes(),
      programTypes: this.programService.getProgramTypes(),
      personTypes: this.programService.getPIInvolvementTypes(),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ element, dataTypes, programTypes, personTypes }) => {
          this.dataTypeOptions.set(
            dataTypes.cdeDataTypes.map((t) => ({ value: t.id, label: t.dataType })),
          );
          this.personTypeOptions.set(
            (personTypes ?? []).map((t) => ({ value: t.id, label: t.name })),
          );
          const types = programTypes.programTypes ?? [];
          this.programTypes.set(types);
          this.name.set(element.name);
          this.definition.set(element.definition);
          this.dataTypeId.set(element.cdeDataTypeId);
          const listItems = (element.cdeListItems ?? [])
            .slice()
            .sort((a, b) => a.displayOrder - b.displayOrder);
          const entries = (element.cdePrograms ?? []).map((e) => ({
            ...e,
            beginDate: e.beginDate ? e.beginDate.split('T')[0] : null,
            endDate: e.endDate ? e.endDate.split('T')[0] : null,
            levelIncidentPI: e.levelIncidentPI ?? 0,
            piInvolvementTypeId: e.piInvolvementTypeId ?? null,
            requiredOptional: e.requiredOptional ?? null,
          }));
          this.listItems.set(listItems);
          this.cdeProgramEntries.set(entries);
          const categoryLinks = element.cdeSituationTaxonomyMembers;
          this.categoriesSupported.set(Array.isArray(categoryLinks));
          this.categoryLinks.set(categoryLinks ?? []);
          if (Array.isArray(categoryLinks)) this.loadCategoryOptions();
          this.initialValues.set({
            name: element.name,
            definition: element.definition,
            dataTypeId: element.cdeDataTypeId,
            listItems: JSON.stringify(listItems.map((i) => i.value)),
            entries: JSON.stringify(
              [...entries]
                .map((e) => ({
                  id: e.programId,
                  b: e.beginDate,
                  en: e.endDate,
                  l: e.levelIncidentPI,
                  pi: e.piInvolvementTypeId,
                  r: e.requiredOptional,
                }))
                .sort((a, b) => a.id.localeCompare(b.id)),
            ),
            categories: categoryKey(categoryLinks ?? []),
          });
          this.loading.set(false);

          if (types.length > 0) {
            this.selectProgramType(types[0].id);
          }
        },
        error: () => {
          this.name.set(this.element().elementName);
          this.loading.set(false);
        },
      });
  }

  protected selectProgramType(typeId: string): void {
    this.programTypeFilter.set(typeId);
    this.loadingPrograms.set(true);
    this.programService
      .getPrograms({ pageSize: 100, pageIndex: 0, filter: '' }, typeId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.programs.set(res.programs ?? []);
          this.loadingPrograms.set(false);
        },
        error: () => this.loadingPrograms.set(false),
      });
  }

  protected programName(programId: string): string {
    return this.programs().find((p) => p.id === programId)?.name ?? programId;
  }

  protected addProgramEntry(id: string): void {
    if (!id) return;
    if (this.cdeProgramEntries().some((e) => e.programId === id)) return;
    this.cdeProgramEntries.update((entries) => [
      ...entries,
      {
        programId: id,
        beginDate: null,
        endDate: null,
        displayOrder: entries.length,
        levelIncidentPI: 0,
        piInvolvementTypeId: null,
        requiredOptional: null,
      },
    ]);
  }

  protected removeProgramEntry(entry: CdeProgramEntry): void {
    this.cdeProgramEntries.update((entries) => entries.filter((e) => e !== entry));
  }

  protected updateProgramDates(entry: CdeProgramEntry, range: DateRange): void {
    this.cdeProgramEntries.update((entries) =>
      entries.map((e) =>
        e === entry ? { ...e, beginDate: range.from || null, endDate: range.to || null } : e,
      ),
    );
  }

  protected updateEntryLevel(entry: CdeProgramEntry, level: number): void {
    this.cdeProgramEntries.update((entries) =>
      entries.map((e) =>
        e === entry
          ? {
              ...e,
              levelIncidentPI: level,
              piInvolvementTypeId: level === 0 ? null : e.piInvolvementTypeId,
            }
          : e,
      ),
    );
  }

  protected updateEntryRequired(entry: CdeProgramEntry, checked: boolean): void {
    this.cdeProgramEntries.update((entries) =>
      entries.map((e) => (e === entry ? { ...e, requiredOptional: checked ? 1 : 0 } : e)),
    );
  }

  protected updateEntryPIType(entry: CdeProgramEntry, piInvolvementTypeId: string | null): void {
    this.cdeProgramEntries.update((entries) =>
      entries.map((e) => (e === entry ? { ...e, piInvolvementTypeId } : e)),
    );
  }

  protected onDataTypeChanged(value: string): void {
    if (value !== this.dataTypeId()) {
      this.listItems.set([]);
      this.newItemRange.set({ from: '', to: '' });
    }
    this.dataTypeId.set(value);
  }

  protected addItem(input: HTMLInputElement): void {
    const val = input.value.trim();
    if (!val || this.listItems().some((i) => i.value === val)) return;
    this.listItems.update((items) => [
      ...items,
      { value: val, beginDate: null, endDate: null, displayOrder: items.length },
    ]);
    input.value = '';
  }

  protected addDateItem(): void {
    const { from, to } = this.newItemRange();
    if (!from && !to) return;
    const value = [from, to].filter(Boolean).join(' – ');
    this.listItems.update((items) => [
      ...items,
      { value, beginDate: from || null, endDate: to || null, displayOrder: items.length },
    ]);
    this.newItemRange.set({ from: '', to: '' });
  }

  protected onItemKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      this.addItem(event.target as HTMLInputElement);
    }
  }

  protected startEdit(idx: number, value: string): void {
    this.editingIndex.set(idx);
    this.editingValue.set(value);
  }

  protected confirmEdit(idx: number): void {
    const val = this.editingValue().trim();
    if (!val) return;
    this.listItems.update((items) =>
      items.map((item, i) => (i === idx ? { ...item, value: val } : item)),
    );
    this.editingIndex.set(null);
    this.editingValue.set('');
  }

  protected cancelEdit(): void {
    this.editingIndex.set(null);
    this.editingValue.set('');
  }

  protected onDragStart(idx: number): void {
    this.dragIndex = idx;
  }

  protected onDragOver(e: DragEvent, idx: number): void {
    e.preventDefault();
    this.dragOverIndex.set(idx);
  }

  protected onDrop(idx: number): void {
    const from = this.dragIndex;
    this.dragIndex = null;
    this.dragOverIndex.set(null);
    if (from === null || from === idx) return;
    this.listItems.update((items) => {
      const arr = [...items];
      const [item] = arr.splice(from, 1);
      arr.splice(idx, 0, item);
      return arr;
    });
  }

  protected onDragEnd(): void {
    this.dragIndex = null;
    this.dragOverIndex.set(null);
  }

  protected removeItem(item: CdeListItem): void {
    this.listItems.update((items) => items.filter((i) => i !== item));
  }

  /** Every situation taxonomy's categories, labelled with the taxonomy (a category can be in several). */
  private loadCategoryOptions(): void {
    this.loadingCategories.set(true);
    this.taxonomyService
      .getSituationTaxonomies()
      .pipe(
        switchMap((res) => {
          const taxonomies = res.situationTaxonomies ?? [];
          if (!taxonomies.length) return of([]);
          return forkJoin(
            taxonomies.map((t) =>
              this.taxonomyMemberService
                .getSituationTaxonomyMembers(t.id, { pageSize: 500, pageIndex: 0 })
                .pipe(
                  map((m) =>
                    (m.situationTaxonomiesMembers ?? []).map(
                      (member) =>
                        [member.id, `${member.situationCategoryName} (${t.name})`] as const,
                    ),
                  ),
                ),
            ),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (groups) => {
          this.categoryLabels.set(new Map(groups.flat()));
          this.loadingCategories.set(false);
        },
        error: () => this.loadingCategories.set(false),
      });
  }

  protected categoryLabel(link: CdeSituationTaxonomyMember): string {
    if (link.situationCategoryName) {
      return link.situationTaxonomyName
        ? `${link.situationCategoryName} (${link.situationTaxonomyName})`
        : link.situationCategoryName;
    }
    return this.categoryLabels().get(link.situationTaxonomyMemberId ?? '') ?? 'Unknown category';
  }

  protected addCategory(memberId: string): void {
    if (!memberId) return;
    if (this.categoryLinks().some((l) => l.situationTaxonomyMemberId === memberId)) return;
    this.categoryLinks.update((links) => [
      ...links,
      {
        situationTaxonomyMemberId: memberId,
        configuredDataElementId: this.element().id,
        beginDate: todayIso(),
        endDate: null,
        displayOrder: links.length + 1,
      },
    ]);
  }

  protected setCategoryRequired(memberId: string | undefined, required: boolean): void {
    this.categoryLinks.update((links) =>
      links.map((l) =>
        l.situationTaxonomyMemberId === memberId ? { ...l, requiredOptional: required ? 1 : 0 } : l,
      ),
    );
  }

  protected removeCategory(memberId: string | undefined): void {
    this.categoryLinks.update((links) =>
      links.filter((l) => l.situationTaxonomyMemberId !== memberId),
    );
  }

  protected onSubmit(): void {
    this.showErrors.set(true);
    if (!this.name().trim() || !this.dataTypeId()) return;
    if (this.cdeProgramEntries().some((e) => !e.beginDate)) return;

    this.submitting.set(true);
    const toDateTime = (d: string | null) => (d ? `${d}T00:00:00` : null);
    const isDateType = this.listInputType() === 'date';
    this.dataElementService
      .updateDataElement({
        id: this.element().id,
        name: this.name().trim(),
        definition: this.definition(),
        cdeDataTypeId: this.dataTypeId(),
        cdeListItems: this.listItems().map((i, idx) => ({
          ...i,
          displayOrder: idx + 1,
          beginDate: isDateType ? toDateTime(i.beginDate) : null,
          endDate: isDateType ? toDateTime(i.endDate) : null,
        })),
        cdePrograms: this.cdeProgramEntries().map((e, idx) => ({
          ...e,
          displayOrder: idx + 1,
          beginDate: toDateTime(e.beginDate),
          endDate: toDateTime(e.endDate),
          levelIncidentPI: e.levelIncidentPI ?? 0,
          piInvolvementTypeId: e.piInvolvementTypeId ?? null,
          requiredOptional: e.requiredOptional ?? null,
        })),
        ...(this.categoriesSupported() && {
          cdeSituationTaxonomyMembers: this.categoryLinks().map((l, idx) => ({
            ...l,
            displayOrder: l.displayOrder ?? idx + 1,
          })),
        }),
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.toastService.show('Data element updated successfully');
          this.saved.emit();
        },
        error: (err) => {
          this.toastService.show(apiErrorMessage(err, 'Failed to update data element'), 'error');
          this.submitting.set(false);
        },
      });
  }

  protected onRemove(): void {
    if (!this.confirmRemove()) {
      this.confirmRemove.set(true);
      return;
    }
    this.removing.set(true);
    this.dataElementService
      .deleteDataElement(this.element().id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.toastService.show('Data element deleted successfully');
          this.removed.emit();
        },
        error: (err) => {
          this.toastService.show(apiErrorMessage(err, 'Failed to delete data element'), 'error');
          this.removing.set(false);
          this.confirmRemove.set(false);
        },
      });
  }

  protected piTypeLabelFor(id: string | null | undefined): string {
    if (!id) return 'All';
    return this.personTypeOptions().find((o) => o.value === id)?.label ?? 'All';
  }

  protected closeLevelEntryDropdown(): void {
    setTimeout(() => this.openLevelEntryProgramId.set(null), 150);
  }

  protected closePITypeEntryDropdown(): void {
    setTimeout(() => this.openPITypeEntryProgramId.set(null), 150);
  }
}

/** Order-insensitive key of the linked categories, for change detection. */
function categoryKey(links: CdeSituationTaxonomyMember[]): string {
  return links
    .map((l) => `${l.situationTaxonomyMemberId ?? ''}:${l.requiredOptional === 1}`)
    .sort()
    .join(',');
}

function todayIso(): string {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}T00:00:00`;
}
